"use client"

import type { FormProps } from "antd"
import { Form, message, Spin } from "antd"
import Title from "antd/es/typography/Title"
import dayjs from "dayjs"
import { t } from "i18next"
import { useCallback, useEffect, useState } from "react"
import "react-quill/dist/quill.snow.css"
import { DataSettingItem, MediaData, TagOption, TourItem } from "../../../../lib/config/types"

import { TOURS } from "@/constants/admin-paths"
import { getSettings } from "@/lib/navigation-services"
import { BikeTypeOption } from "@/types/types"
import { useRouter, useSearchParams } from "next/navigation"
import { useSelector } from "react-redux"
import { RootState } from "../../auth/services/redux/store"
import { fetchBikeTypes } from "../../bike_type/services/bike_type"
import Categories from "../../post/pages/Categories"
import Tags from "../../post/pages/Tags"
import { checkSlug, createNewTourV2, fetchTourV2ById, updateTourV2 } from "../services/tours"
import FeaturedImage from "./FeaturedImage"
import Publish from "./Publish"
import TourForm from "./TourForm"

const normalizeTourName = (name: string): string => {
  if (!name) return ""
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

const UpsertTour = () => {
  const router = useRouter()
  const [form] = Form.useForm()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const account = useSelector((state: RootState) => state.account)

  const [tour, setTour] = useState<TourItem | null>(null)
  const [status, setStatus] = useState("Draft")
  const [language, setLanguages] = useState("en")

  const [selectedImage, setSelectedImage] = useState<MediaData[]>([])
  const [loading, setLoading] = useState(false)

  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [linkWeb, setLinkWeb] = useState<string>("")
  const [bikeTypes, setBikeTypes] = useState<BikeTypeOption[]>([])
  const [loadingBikeTypes, setLoadingBikeTypes] = useState(false)

  const loadBikeTypes = async () => {
    try {
      setLoadingBikeTypes(true)
      const response = await fetchBikeTypes()
      if (response?.data) {
        setBikeTypes(response.data)
      }
    } catch (error) {
      console.error("Error loading bike types:", error)
    } finally {
      setLoadingBikeTypes(false)
    }
  }

  const fetchSetting = async () => {
    try {
      const response = await getSettings()
      const link = response.find((item: DataSettingItem) => item.attributes.key === "linkwebsite")?.attributes.value
      if (link) {
        setLinkWeb(link)
      }
    } catch (error) {
      console.error("Error fetching setting data:", error)
    }
  }

  useEffect(() => {
    fetchSetting()
    loadBikeTypes()
  }, [])

  const onFinish: FormProps<any>["onFinish"] = async () => {
    const values = form.getFieldsValue()

    const payload = {
      feature_img: selectedImage || [],
      category: selectedCategories || [],
      tags: selectedTags || [],
      locale: language,
      author: account.user,
      publishedAt: status === "Draft" ? null : new Date().toISOString(),

      is_new: values.is_new,
      is_most_popular: values.is_most_popular,
      name: values.name,
      normalize_name: normalizeTourName(values.name || ""),
      description: values.description,
      price_overview: values.price_overview,
      price_note: values.price_note,
      slug: values.slug,
      itinerary: values.itinerary,
      related_tours: values.related_tours,
      duration: values.duration,
      location: values.location,
      distance: values.distance,
      difficulty: values.difficulty,
      inclusions: values.inclusions,
      highlights: values.highlights,
      elevation: values.elevation,
      tour_detail: values.tour_detail,
      start_time: values.start_time,
      show_bike_type: values.show_bike_type || false,
      bike_types: Array.isArray(values.bike_types) ? values.bike_types.map((id: number) => ({ id })) : [],
      overview: { label: values.over_view_label, description: values.over_view_description },
      seo: {
        metaTitle: values.metaTitle || values.title,
        metaDescription: values.metaDescription || values.title,
        keywords: values.keywords,
        metaRobots: values.metaRobots,
        metaViewport: values.metaViewport,
        structuredData: (() => {
          if (typeof values.structuredData === "string") {
            try {
              return values.structuredData.trim() ? JSON.parse(values.structuredData) : {}
            } catch (e) {
              console.error("Invalid JSON in structuredData:", e)
              return {}
            }
          }
          return {}
        })(),
        canonicalURL: values.canonicalURL,
      },
    }

    const isSlugChanged = values.slug !== tour?.attributes?.slug
    if (isSlugChanged) {
      const isSlugUnique = async (slug: string): Promise<boolean> => {
        try {
          const response = await checkSlug(slug)
          return response.data.length === 0
        } catch {
          return false
        }
      }

      const isUnique = await isSlugUnique(values.slug || "")

      if (!isUnique) {
        message.error(t("The path already exists. Please choose another path"))
        return
      }
    }
    try {
      const response = id ? await updateTourV2(Number(id), { data: payload }) : await createNewTourV2({ data: payload })

      // Chỉ redirect khi create thành công
      if (!id && response) {
        message.success(t("Tour added successfully!"))
        form.resetFields()
        router.push(`/admin${TOURS.LIST.PATH}`)
      } else if (id && response) {
        // Edit mode: giữ nguyên trang, chỉ hiển thị message success
        message.success(t("Tour update successfully!"))
      }
    } catch (error: any) {
      console.error("Error updating tour:", error)
      // BadRequest/RequestError có structure: { data: { data: null, error: {...} }, message: "..." }
      // error.data từ BadRequest chứa response.data từ API: { data: null, error: {...} }
      // Cần lấy error.data.error để có error object từ API
      const apiErrorData = error?.data?.error || error?.data

      // Ưu tiên message từ API error, sau đó mới đến error.message (axios message)
      // Nếu apiErrorData là object có message, dùng message đó
      // Nếu không, dùng error.message nhưng ưu tiên message từ API error object
      const errorMessage =
        apiErrorData?.message ||
        (apiErrorData && typeof apiErrorData === "object" && "message" in apiErrorData ? apiErrorData.message : null) ||
        error?.message ||
        t("Unable to update page. Please try again")

      // Xử lý validation errors - set errors cho các field
      if (apiErrorData?.details?.errors && Array.isArray(apiErrorData.details.errors)) {
        const fieldErrors = apiErrorData.details.errors.map((err: any) => {
          // Lấy field name từ path (path có thể là array như ["price_overview"])
          const fieldName = Array.isArray(err.path) ? err.path[0] : err.path
          return {
            name: fieldName,
            errors: [err.message || errorMessage],
          }
        })

        // Set errors cho các field
        form.setFields(fieldErrors)

        // Scroll đến field đầu tiên bị lỗi
        if (fieldErrors.length > 0) {
          const firstErrorField = fieldErrors[0].name
          setTimeout(() => {
            // Tìm field bằng nhiều cách
            let errorElement: HTMLElement | null = null

            // Cách 1: Tìm input/select có name attribute
            errorElement = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement

            // Cách 2: Tìm input/select có id
            if (!errorElement) {
              errorElement = document.querySelector(`#${firstErrorField}`) as HTMLElement
            }

            // Cách 3: Tìm Form.Item chứa field (Ant Design thường có class ant-form-item)
            if (!errorElement) {
              const formItem = document.querySelector(`.ant-form-item[data-field="${firstErrorField}"]`)
              if (formItem) {
                errorElement = formItem.querySelector("input, select, textarea") as HTMLElement
              }
            }

            // Cách 4: Tìm input bên trong Form.Item có label chứa field name
            if (!errorElement) {
              const allFormItems = document.querySelectorAll(".ant-form-item")
              for (const item of allFormItems) {
                const input = item.querySelector(
                  `input[name="${firstErrorField}"], select[name="${firstErrorField}"], textarea[name="${firstErrorField}"]`,
                )
                if (input) {
                  errorElement = input as HTMLElement
                  break
                }
              }
            }

            // Cách 5: Tìm bằng field name trong Ant Design Form (sử dụng fieldId)
            if (!errorElement) {
              const fieldId = `rc_select_${firstErrorField}`
              errorElement = document.querySelector(`#${fieldId}`) as HTMLElement
            }

            if (errorElement) {
              errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
              // Focus vào field nếu là input
              if (
                errorElement instanceof HTMLElement &&
                (errorElement.tagName === "INPUT" ||
                  errorElement.tagName === "SELECT" ||
                  errorElement.tagName === "TEXTAREA")
              ) {
                errorElement.focus()
              }
            } else {
              // Nếu không tìm thấy, log để debug
              console.warn(`Could not find field element for: ${firstErrorField}`)
            }
          }, 300) // Tăng timeout để đảm bảo DOM đã render
        }
      }

      // Chỉ hiển thị message nếu không phải validation error (để tránh duplicate message)
      // Hoặc hiển thị message từ API nếu có
      if (apiErrorData?.message) {
        message.error(apiErrorData.message)
      } else if (!apiErrorData?.details?.errors) {
        // Chỉ hiển thị generic message nếu không có validation errors
        message.error(errorMessage)
      }
      // Không redirect khi có lỗi
    } finally {
      setLoading(false)
    }
  }

  const onChangeCategories = (checkedValues: string[]) => {
    setSelectedCategories(checkedValues)
  }
  const onChangeTags = (checkedValues: string[]) => {
    setSelectedTags(checkedValues)
  }
  const handleSelectMedia = (media: MediaData[]) => {
    setSelectedImage(media)
  }

  const handleFetchTour = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await fetchTourV2ById(id)
        const data = response.data.attributes

        setTour(response.data)
        if (response?.data?.attributes?.publishedAt) {
          setStatus("Published")
        } else {
          setStatus("Draft")
        }
        form.setFieldsValue({
          ...response.data.attributes,
          over_view_description: data.overview.description,
          over_view_label: data.overview.label,
          metaTitle: data.seo.metaTitle,
          metaDescription: data.seo.metaDescription,
          canonicalURL: data.seo.canonicalURL,
          keywords: data.seo.keywords,
          metaRobots: data.seo.metaRobots,
          metaViewport: data.seo.metaViewport,
          start_time: data.start_time ? dayjs(data.start_time) : undefined,
          show_bike_type: data.show_bike_type || false,
          bike_types: data.bike_types.data.map((item: BikeTypeOption) => item.id),
        })
        form.setFieldValue("structuredData", JSON.stringify(data.seo.structuredData || {}))
        setSelectedCategories(data?.category?.data?.id ? [data.category.data.id] : [])
        setSelectedTags(data.tags.data.map((tag: TagOption) => tag.id))
        setLanguages(data?.locale)
        setSelectedImage(data?.feature_img)
      } catch {
        message.error(t("Error fetching tour"))
      } finally {
        setLoading(false)
      }
    },
    [form],
  )

  useEffect(() => {
    if (id) {
      handleFetchTour(Number(id))
    }
  }, [id, handleFetchTour])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <div className="flex space-x-4 items-center">
            <Title level={2}>{t(`${id ? "Edit" : "Add New"} Tour`)}</Title>
          </div>
          <div className="flex justify-between">
            <TourForm form={form} onFinish={onFinish} bikeTypes={bikeTypes} loadingBikeTypes={loadingBikeTypes} />
            <div className=" w-[22%] pt-2 ">
              <Publish
                onSubmit={form.submit}
                isCreateEdit={!id}
                setStatus={setStatus}
                status={status}
                language={language}
                setLanguages={setLanguages}
              />
              <FeaturedImage selectedMedia={selectedImage} setSelectedMedia={handleSelectMedia} />
              <Categories selectedCategories={selectedCategories} onChangeCategories={onChangeCategories} />
              <Tags selectedTags={selectedTags} onChangeTags={onChangeTags} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default UpsertTour
